import React, { FunctionComponent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { Tag, Position, Intent } from '@blueprintjs/core';
import { Popover2 } from "@blueprintjs/popover2";
import './ListFilterItem.scss';
import { IFilter } from '../../../models';
import { useSelector } from 'react-redux';
import { IState } from '../../../store/reducers';
import ListFilterMenuContent from './ListFilterMenuContent';
import { NO_VALUE_OPERATORS, RELATIVE_DAYS_OPERATORS } from './types';

type RemoveCallback = (event: MouseEvent<HTMLButtonElement>) => void;
type ApplyCallback = (filter: IFilter, index: number) => void;

type ListFilterItemComponentProps = {
  filter: IFilter;
  configured?: boolean;
  onRemove?: RemoveCallback;
  onApply?: ApplyCallback;
  index: number;
};


const ListFilterItemComponent: FunctionComponent<ListFilterItemComponentProps> = ({
  filter, configured, onRemove, onApply, index
}) => {
  const allTags = useSelector((state: IState) => state.tags.tags);
  const allUsers = useSelector((state: IState) => state.users.users);
  const allFolders = useSelector((state: IState) => state.folder.folders);
  const [isOpen, setIsOpen] = useState(false)
  const onHandleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRemove && onRemove(event);
  };

  useEffect(() => {
    if (!configured) {
      setIsOpen(true)
    }
  }, [])

  const filterLabel = useMemo(() => {
    const isUserSelect = ['created_by', 'changed_by'].includes(filter.name)
    const isFolderSelect = ['folder_id'].includes(filter.name)
    let valueLabel = filter.value
    if (filter.name === 'tags') {
      let ids: any[] = []
      if (!Array.isArray(filter.value)) {
        ids = filter.value?.split(',') || [];
      } else {
        ids = filter.value
      }
      const selectedTags = allTags.filter(t => ids.includes(t.uid))
      const tagNames = selectedTags.map(t => t.name).join(', ')
      valueLabel = tagNames;
    }
    if (isUserSelect) {
      const user = allUsers?.find(u => u.uid === filter.value)
      valueLabel = `${user?.first_name} ${user?.last_name}`
    }
    if (isFolderSelect) {
      const folder = allFolders?.find((f: any) => f.id === filter.value)
      valueLabel = folder?.name
    }
    if (filter.value && filter.opr) {
      if (isUserSelect || isFolderSelect) {
        return `${filter.optionName} '${valueLabel}'`
      }
      if (NO_VALUE_OPERATORS.includes(filter.opr)) {
        return `${filter.label} ${filter.optionName}`
      }
      if (RELATIVE_DAYS_OPERATORS.includes(filter.opr) && valueLabel) {
        if (+valueLabel > 1) {
          return `${filter.label} ${filter.optionName} '${valueLabel}' days ago`
        } else {
          return `${filter.label} ${filter.optionName} '${valueLabel}' day ago`
        }
      }
      return `${filter.label} ${filter.optionName} '${valueLabel}'`
    }
    return filter.label
  }, [filter])

  const onHandleApply = (condition: { type: string; value?: any; }) => {
    onApply && onApply({
      ...filter,
      opr: condition.type,
      value: condition.value
    }, index);
    setIsOpen(false)
  };

  return (
    <Popover2
      content={<ListFilterMenuContent filter={filter} onHandleApply={onHandleApply} />}
      position={Position.BOTTOM_RIGHT}
      canEscapeKeyClose={true}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Tag
        data-cy='filterTags'
        intent={configured ? Intent.SUCCESS : undefined} // Intent.NONE
        minimal={true}
        onClick={() => setIsOpen(prev => !prev)}
        onRemove={(event) => onHandleRemove(event)}
      >
        {filterLabel}
      </Tag>
    </Popover2>
  );
};

export default ListFilterItemComponent;
